import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../contexts/CartContext';
import { ordersAPI } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  CheckCircle2, 
  Truck, 
  Package, 
  XCircle,
  Filter,
  ShoppingBag,
  ArrowRight,
  Loader2,
  Printer
} from 'lucide-react';
import CustomSelect from './CustomSelect';
// Uses global header via Layout

const UserOrders = () => {
  const { loadCart } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reordering, setReordering] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getAll();
      setOrders(response.data);
    } catch (err) {
      setError('Failed to fetch orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders based on selected status
  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') {
      return orders;
    }
    return orders.filter(order => order.status === statusFilter);
  }, [orders, statusFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return ClipboardList;
      case 'confirmed': return CheckCircle2;
      case 'shipped': return Truck;
      case 'delivered': return Package;
      case 'cancelled': return XCircle;
      default: return ClipboardList;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReorder = async (orderId) => {
    setReordering(prev => ({ ...prev, [orderId]: true }));
    
    try {
      const response = await ordersAPI.reorder(orderId);
      
      if (response.status === 200) {
        // Reload cart from server after reorder
        await loadCart();
        
        // Show detailed success message
        let message = response.data.message;
        
        // Add details about unavailable items
        if (response.data.unavailable_items && response.data.unavailable_items.length > 0) {
          message += '\n\nUnavailable items:\n';
          response.data.unavailable_items.forEach(item => {
            message += `• ${item.name} (${item.reason || 'Out of stock'})\n`;
          });
        }
        
        // Add details about low stock items
        if (response.data.low_stock_items && response.data.low_stock_items.length > 0) {
          message += '\n\nItems with reduced quantities:\n';
          response.data.low_stock_items.forEach(item => {
            message += `• ${item.name}: ${item.added}/${item.requested} added\n`;
          });
        }
        
        alert(message);
        
        // Navigate to cart/checkout
        navigate('/checkout');
      } else {
        throw new Error('Failed to reorder');
      }
    } catch (err) {
      console.error('Error reordering:', err);
      
      // Handle different types of errors
      if (err.response?.status === 404) {
        alert('Order not found. This order may have been deleted or you may not have permission to access it.');
      } else if (err.response?.data?.error) {
        alert(`Reorder failed: ${err.response.data.error}`);
      } else {
        alert('Failed to reorder items. Please try again.');
      }
    } finally {
      setReordering(prev => ({ ...prev, [orderId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {/* Filter Section */}
          {orders.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200 overflow-visible">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Filter by status:
                  </span>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px] relative">
                  <CustomSelect
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All Orders' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'confirmed', label: 'Confirmed' },
                      { value: 'shipped', label: 'Shipped' },
                      { value: 'delivered', label: 'Delivered' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                    className=""
                  />
                </div>
              </div>
            </div>
          )}

          {/* Orders List */}
          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-200">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                  <ShoppingBag className="w-12 h-12 text-gray-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No orders yet</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                You haven't placed any orders yet. Start shopping to see your orders here.
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-medium hover:bg-green-700 transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                Start Shopping
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-200">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                  <Filter className="w-12 h-12 text-gray-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No orders found</h3>
              <p className="text-gray-600 mb-8">
                No orders match the selected filter. Try selecting a different status.
              </p>
              <button
                onClick={() => setStatusFilter('all')}
                className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-medium hover:bg-green-700 transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                Show All Orders
              </button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {filteredOrders.map((order) => {
                const StatusIcon = getStatusIcon(order.status);
                return (
                  <div 
                    key={order.id} 
                    className="bg-white rounded-lg shadow-sm hover:shadow-md border border-gray-200 transition-shadow duration-300 overflow-hidden"
                  >
                    {/* Order Header */}
                    <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                              Order #{order.id}
                            </h3>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border ${getStatusColor(order.status)}`}>
                              <StatusIcon className="w-3.5 h-3.5" />
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            Placed on {formatDate(order.created_at)}
                          </p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                            {order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {order.order_items.length} item{order.order_items.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="px-4 sm:px-6 py-4 sm:py-5">
                      <div className="space-y-3 sm:space-y-4">
                        {order.order_items.map((item) => (
                          <div 
                            key={item.id} 
                            className="flex items-center gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                          >
                            <img
                              src={item.product.image_url}
                              alt={item.product.name}
                              className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg border-2 border-white shadow-sm flex-shrink-0"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 truncate">
                                {item.product.name}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600 mb-1">{item.product.category}</p>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs sm:text-sm text-gray-600">
                                  Qty: <span className="font-medium text-gray-900">{item.quantity}</span>
                                </span>
                                <span className="text-xs sm:text-sm text-gray-500">
                                  {item.product.formatted_price} each
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-base sm:text-lg font-bold text-gray-900">
                                {item.formatted_total_price}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Actions */}
                    <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <div className="flex flex-wrap gap-3">
                          <Link
                            to={`/orders/${order.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                          >
                            Track Order
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                          {(order.payment_status === 'success' || order.payment_status === 'paid' || ['confirmed', 'shipped', 'delivered'].includes(order.status)) && (
                            <Link
                              to={`/orders/${order.id}/invoice`}
                              target="_blank"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors duration-200 shadow-sm hover:shadow-md"
                            >
                              <Printer className="w-3.5 h-3.5" strokeWidth={1.5} />
                              Invoice
                            </Link>
                          )}
                          {order.status !== 'cancelled' && (
                            <button 
                              onClick={() => handleReorder(order.id)}
                              disabled={reordering[order.id]}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm hover:shadow-md disabled:hover:shadow-sm"
                            >
                              {reordering[order.id] ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <Package className="w-3.5 h-3.5" />
                                  Reorder
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserOrders;
